import { useCallback, useEffect, useRef, useState } from 'react';
import { pairTradingApi, PairListItem } from '../services/pairTradingApi';

/** Status + PnL of every pair is cheap to read; poll it lightly. */
const IDLE_POLL_MS = 30_000;
/** An ENDING pair settles within seconds to minutes — watch it closely until ENDED. */
const ENDING_POLL_MS = 10_000;

interface ListState {
    portfolioId: string;
    pairs: PairListItem[];
    currentDataConfigId: string | null;
}

/**
 * Every pair config on a stock portfolio (GET /pair/list/:portfolioId).
 *
 * `pairs` is null until the list for THIS portfolio has loaded — the state is tagged
 * with the portfolio it belongs to, so a selection is never resolved against the
 * previous portfolio's pairs in the render right after a switch.
 */
export function usePairList(portfolioId: string | null) {
    const [state, setState] = useState<ListState | null>(null);
    const [error, setError] = useState<string | null>(null);
    const portfolioRef = useRef(portfolioId);
    portfolioRef.current = portfolioId;

    const refresh = useCallback(async () => {
        const forPortfolio = portfolioId;
        if (!forPortfolio) return;
        try {
            const res = await pairTradingApi.listPairs(forPortfolio);
            if (portfolioRef.current !== forPortfolio) return;
            setState({
                portfolioId: forPortfolio,
                pairs: res.pairs ?? [],
                currentDataConfigId: res.currentDataConfigId ?? null,
            });
            setError(null);
        } catch (e: any) {
            if (portfolioRef.current !== forPortfolio) return;
            // Keep the last good list. Never fall back to "no pairs": that would open an
            // empty New pair form next to a live pair the page simply failed to read.
            setError(e?.response?.data?.message ?? e?.message ?? 'Failed to load pairs');
        }
    }, [portfolioId]);

    useEffect(() => {
        setError(null);
        void refresh();
    }, [refresh]);

    const current = state && state.portfolioId === portfolioId ? state : null;
    const anyEnding = !!current?.pairs.some((p) => p.lifecycleStatus === 'ENDING');
    useEffect(() => {
        if (!portfolioId) return;
        const timer = setInterval(() => void refresh(), anyEnding ? ENDING_POLL_MS : IDLE_POLL_MS);
        return () => clearInterval(timer);
    }, [portfolioId, refresh, anyEnding]);

    return {
        pairs: current ? current.pairs : null,
        currentDataConfigId: current ? current.currentDataConfigId : null,
        error,
        refresh,
    };
}

export default usePairList;
